import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import { StatusTimeline } from '../components/StatusTimeline';
import { StatusActions } from '../components/StatusActions';
import { CommitteeDecisionPanel } from '../components/CommitteeDecisionPanel';
import { ChairOverridePanel } from '../components/ChairOverridePanel';
import { CompetencyLevelForm } from '../components/CompetencyLevelForm';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GuidelinesSidebar } from '../components/GuidelinesSidebar';
import { MentionInput } from '../components/MentionInput';
import { getStatusLabel } from '../lib/status';
import { LOCKED_PROPOSAL_STATUSES } from '../lib/constants';
import { useToastStore } from '../store/toastStore';
import { User, Calendar, Building2, Clock, AlertCircle, MessageSquare, Send, Gavel, Link2, Rocket, ExternalLink } from 'lucide-react';

interface Proposal {
  id: string; title: string; department: string; problem_statement: string;
  proposed_solution: string; expected_impact: string; current_status: string;
  urgency: string; urgency_reason: string; status: string;
  submitter_id: string; submitter_name: string; submitter_email: string; submitted_at: string;
  competency_level: number | null;
  building_type: string; ai_maturity_level: string; doc_link: string;
  hld_link: string; git_repo_url: string; staging_url: string; production_url: string;
  other_details: string; where_stuck: string;
  tech_frontend: string[]; tech_backend: string[]; tech_database: string[];
  tech_ai_tools: string[]; tech_integrations: string[];
  support_needs?: { support_type: string; other_text: string | null }[];
}

interface Comment {
  id: string; body: string; author_name: string; author_roles: string[];
  recommendation: string | null; created_at: string; parent_comment_id: string | null;
}

interface Verdict {
  decision: string;
  rationale: string;
  conditions: string | null;
  created_at: string;
  decided_at: string;
}

const VERDICT_LABELS: Record<string, string> = {
  approved: 'Approved',
  approved_with_conditions: 'Approved with Conditions',
  parked: 'Parked',
  rejected: 'Rejected',
};

const REVIEW_STATUSES = ['under_triage', 'meeting_scheduled', 'in_review', 'awaiting_decision'] as const;
const ARTIFACT_EDIT_STATUSES = [
  ...REVIEW_STATUSES,
  'awaiting_deployment',
  'approved',
  'approved_with_conditions',
] as const;
const ARTIFACT_VISIBLE_STATUSES = [...ARTIFACT_EDIT_STATUSES, 'shipped'] as const;

export function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [commentError, setCommentError] = useState('');
  const [artifactForm, setArtifactForm] = useState({
    hld_link: '', doc_link: '', git_repo_url: '', staging_url: '', other_details: '',
  });
  const [savingArtifacts, setSavingArtifacts] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [submittingDeployment, setSubmittingDeployment] = useState(false);
  const showToast = useToastStore((s) => s.show);

  const isReviewer = user?.roles.some((r) => ['reviewer', 'chair', 'admin'].includes(r));
  const isChair = user?.roles.some((r) => ['chair'].includes(r));
  // Committee members and admins see full internal review data. Submitters see
  // discussion/comments only on proposals they own (checked after proposal loads).
  const canSeeInternal = user?.roles.some((r) => ['reviewer', 'chair', 'admin'].includes(r));

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [proposalRes, commentsRes] = await Promise.all([
        api.get<{ proposal: Proposal; supportNeeds: any[]; verdict: Verdict | null }>(`/proposals/${id}`),
        api.get<{ comments: Comment[] }>(`/comments?proposal_id=${id}`),
      ]);
      setProposal({ ...proposalRes.proposal, support_needs: proposalRes.supportNeeds });
      setVerdict(proposalRes.verdict);
      setComments(commentsRes.comments);
      const p = proposalRes.proposal;
      setArtifactForm({
        hld_link: p.hld_link || '',
        doc_link: p.doc_link || '',
        git_repo_url: p.git_repo_url || '',
        staging_url: p.staging_url || '',
        other_details: p.other_details || '',
      });
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load proposal');
      setProposal(null);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [id]);

  const postComment = async () => {
    if (!newComment.trim()) return;
    setCommentError('');
    try {
      await api.post('/comments', { proposal_id: id, body: newComment });
      setNewComment('');
      fetchData();
      showToast('Comment posted', 'success');
    } catch (err: any) {
      setCommentError(err.message || 'Failed to post comment');
    }
  };

  const saveArtifacts = async () => {
    setSavingArtifacts(true);
    try {
      await api.patch(`/proposals/${id}/artifacts`, artifactForm);
      showToast('Project details saved', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Failed to save project details', 'error');
    }
    setSavingArtifacts(false);
  };

  const submitDeploymentDetails = async () => {
    if (!artifactForm.git_repo_url.trim()) {
      showToast('Git repo URL is required', 'error');
      return;
    }
    if (!artifactForm.hld_link.trim() && !artifactForm.doc_link.trim()) {
      showToast('HLD link or design doc link is required', 'error');
      return;
    }
    setSubmittingDeployment(true);
    try {
      await api.patch(`/proposals/${id}/deployment-details`, artifactForm);
      showToast('Deployment details submitted — proposal approved!', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Failed to submit deployment details', 'error');
    }
    setSubmittingDeployment(false);
  };

  const markShipped = async () => {
    setShipping(true);
    try {
      await api.patch(`/proposals/${id}/ship`, {});
      showToast('Proposal marked as shipped!', 'success');
      fetchData();
    } catch (err: any) {
      showToast(err.message || 'Failed to mark as shipped', 'error');
    }
    setShipping(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loadError || !proposal) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-muted-foreground">{loadError || 'Proposal not found'}</p>
        <Button variant="outline" onClick={() => window.history.back()}>Go back</Button>
      </div>
    );
  }

  const statusVariant = ['approved', 'approved_with_conditions', 'shipped'].includes(proposal.status) ? 'success' :
    ['rejected'].includes(proposal.status) ? 'destructive' :
    ['awaiting_deployment', 'awaiting_decision', 'needs_clarification'].includes(proposal.status) ? 'warning' : 'info';

  const isOwner = proposal.submitter_id === user?.id;
  const canSeeComments = canSeeInternal || isOwner;
  const commentsClosed = (LOCKED_PROPOSAL_STATUSES as readonly string[]).includes(proposal.status);
  const canSeeVerdict = verdict && (canSeeInternal || isOwner);

  const hasArtifacts = Boolean(
    proposal.hld_link || proposal.git_repo_url || proposal.staging_url ||
    proposal.doc_link || proposal.other_details
  );
  const isReviewStage = (REVIEW_STATUSES as readonly string[]).includes(proposal.status);
  const isAwaitingDeployment = proposal.status === 'awaiting_deployment';
  const canEditArtifacts = (isOwner || canSeeInternal)
    && (ARTIFACT_EDIT_STATUSES as readonly string[]).includes(proposal.status);
  const canSubmitDeployment = isOwner && isAwaitingDeployment;
  const showArtifactsCard = (canSeeInternal || isOwner)
    && ((ARTIFACT_VISIBLE_STATUSES as readonly string[]).includes(proposal.status) || hasArtifacts);
  const showStatusTimeline = canSeeInternal || (isOwner && isAwaitingDeployment);
  const canShip = (isOwner || canSeeInternal) && ['approved', 'approved_with_conditions', 'awaiting_deployment'].includes(proposal.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{proposal.title}</h1>
              <Badge variant={statusVariant}>{getStatusLabel(proposal.status)}</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{proposal.submitter_name}</span>
              <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{proposal.department}</span>
              {proposal.submitted_at && (
                <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(proposal.submitted_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Status Timeline — committee sees full workflow; submitters see it during Deployment stage */}
      {showStatusTimeline && (
        <Card>
          <CardContent className="pt-6 pb-4">
            <StatusTimeline currentStatus={proposal.status} />
          </CardContent>
        </Card>
      )}

      {/* Reviewer Actions */}
      {isReviewer && (
        <StatusActions proposalId={id!} currentStatus={proposal.status} onStatusChanged={fetchData} />
      )}

      {/* Proposal Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content - 2 cols */}
        <div className={`space-y-6 ${canSeeInternal ? 'lg:col-span-2' : 'lg:col-span-2'}`}>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-primary" />
                Problem Statement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">{proposal.problem_statement}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                Proposed Solution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">{proposal.proposed_solution}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <svg className="h-4 w-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                Expected Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">{proposal.expected_impact || '—'}</p>
            </CardContent>
          </Card>

          {proposal.other_details && !showArtifactsCard && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Other Details</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{proposal.other_details}</p>
              </CardContent>
            </Card>
          )}

          {proposal.where_stuck && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Where They're Stuck</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{proposal.where_stuck}</p>
              </CardContent>
            </Card>
          )}

          {proposal.support_needs && proposal.support_needs.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Support Needed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {proposal.support_needs.map((s) => (
                    <Badge key={s.support_type} variant="secondary">
                      {s.support_type}{s.other_text ? `: ${s.other_text}` : ''}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - 1 col */}
        <div className="space-y-4">
          {canSeeInternal && <GuidelinesSidebar compact />}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(proposal as any).building_type && (
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Building Type</p>
                  <p className="text-sm font-medium">
                    {proposal.building_type === 'tool_building' ? 'Tool Building with AI' :
                     proposal.building_type === 'workflow_automation' ? 'Workflow & Automation' :
                     'Other'}
                  </p>
                </div>
              )}
              {(proposal as any).building_type && <Separator />}
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Project Status</p>
                <p className="text-sm font-medium capitalize">
                  {proposal.current_status === 'nearly_complete' ? 'Nearly Complete' :
                   proposal.current_status === 'partial' ? 'Partially Built' :
                   proposal.current_status}
                </p>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground mb-0.5">Timeline</p>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {proposal.urgency === 'two_weeks' ? '2 Weeks' :
                   proposal.urgency === 'one_month' ? '1 Month' :
                   proposal.urgency === 'one_quarter' ? '1 Quarter' :
                   'No Deadline'}
                </p>
                {proposal.urgency_reason && (
                  <p className="text-xs text-muted-foreground mt-1">{proposal.urgency_reason}</p>
                )}
              </div>
              {proposal.ai_maturity_level && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">AI Maturity Level</p>
                    <p className="text-sm font-medium">{proposal.ai_maturity_level}</p>
                  </div>
                </>
              )}
              {canSeeInternal && proposal.competency_level !== null && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Competency Level (Committee)</p>
                    <Badge variant="default">L{proposal.competency_level}</Badge>
                  </div>
                </>
              )}
              {proposal.doc_link && !showArtifactsCard && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Design doc / HLD</p>
                    <a href={proposal.doc_link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline break-all">
                      {proposal.doc_link}
                    </a>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Tech Stack */}
          {(proposal.tech_frontend?.length > 0 || proposal.tech_backend?.length > 0 || proposal.tech_database?.length > 0 || proposal.tech_ai_tools?.length > 0 || proposal.tech_integrations?.length > 0) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Tech Stack</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {proposal.tech_integrations?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Integrations</p>
                    <div className="flex flex-wrap gap-1">{proposal.tech_integrations.map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}</div>
                  </div>
                )}
                {proposal.tech_ai_tools?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">AI Tools</p>
                    <div className="flex flex-wrap gap-1">{proposal.tech_ai_tools.map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}</div>
                  </div>
                )}
                {proposal.tech_database?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Database</p>
                    <div className="flex flex-wrap gap-1">{proposal.tech_database.map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}</div>
                  </div>
                )}
                {proposal.tech_frontend?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Frontend</p>
                    <div className="flex flex-wrap gap-1">{proposal.tech_frontend.map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}</div>
                  </div>
                )}
                {proposal.tech_backend?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Backend</p>
                    <div className="flex flex-wrap gap-1">{proposal.tech_backend.map((t) => <Badge key={t} variant="secondary" className="text-[11px]">{t}</Badge>)}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Project Artifacts */}
      {showArtifactsCard && (
        <Card className={isAwaitingDeployment ? 'border-2 border-primary/30 bg-primary/5' : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" />
              Project Artifacts
              {isReviewStage && (
                <span className="text-xs font-normal text-muted-foreground ml-1">(optional)</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isReviewStage && (
              <p className="text-sm text-muted-foreground">
                Add HLD, repository, and other deployment details if available — these carry forward to the Deployment stage.
              </p>
            )}
            {canSeeInternal && !isOwner && canEditArtifacts && (
              <p className="text-sm text-muted-foreground">
                You can add these on behalf of the submitter if you have them.
              </p>
            )}
            {isAwaitingDeployment && isOwner && (
              <p className="text-sm text-muted-foreground">
                Your proposal was approved by the committee. Provide deployment links to move to approved status.
              </p>
            )}
            {isAwaitingDeployment && canSeeInternal && !isOwner && (
              <p className="text-sm text-amber-900">
                Waiting for {proposal.submitter_name} to submit deployment details. You can save fields below without advancing status.
              </p>
            )}

            {canEditArtifacts ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">HLD / design doc link</Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={artifactForm.hld_link}
                      onChange={(e) => setArtifactForm((f) => ({ ...f, hld_link: e.target.value }))}
                      placeholder="https://..."
                    />
                    {artifactForm.hld_link && /^https?:\/\//i.test(artifactForm.hld_link) && (
                      <a href={artifactForm.hld_link} target="_blank" rel="noopener noreferrer" title="Open in new window" className="shrink-0 text-primary hover:text-primary/80">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Design doc link (if no HLD)</Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={artifactForm.doc_link}
                      onChange={(e) => setArtifactForm((f) => ({ ...f, doc_link: e.target.value }))}
                      placeholder="https://..."
                    />
                    {artifactForm.doc_link && /^https?:\/\//i.test(artifactForm.doc_link) && (
                      <a href={artifactForm.doc_link} target="_blank" rel="noopener noreferrer" title="Open in new window" className="shrink-0 text-primary hover:text-primary/80">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">
                    Git Repository URL
                    {canSubmitDeployment && ' *'}
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={artifactForm.git_repo_url}
                      onChange={(e) => setArtifactForm((f) => ({ ...f, git_repo_url: e.target.value }))}
                      placeholder="https://github.com/..."
                    />
                    {artifactForm.git_repo_url && /^https?:\/\//i.test(artifactForm.git_repo_url) && (
                      <a href={artifactForm.git_repo_url} target="_blank" rel="noopener noreferrer" title="Open in new window" className="shrink-0 text-primary hover:text-primary/80">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">App URL (optional)</Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      value={artifactForm.staging_url}
                      onChange={(e) => setArtifactForm((f) => ({ ...f, staging_url: e.target.value }))}
                      placeholder="https://..."
                    />
                    {artifactForm.staging_url && /^https?:\/\//i.test(artifactForm.staging_url) && (
                      <a href={artifactForm.staging_url} target="_blank" rel="noopener noreferrer" title="Open in new window" className="shrink-0 text-primary hover:text-primary/80">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes / deployment comments</Label>
                  <Textarea
                    value={artifactForm.other_details}
                    onChange={(e) => setArtifactForm((f) => ({ ...f, other_details: e.target.value }))}
                    rows={3}
                    placeholder="Any additional deployment notes..."
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  {canEditArtifacts && (
                    <Button
                      variant={canSubmitDeployment ? 'outline' : 'default'}
                      onClick={saveArtifacts}
                      disabled={savingArtifacts}
                    >
                      {savingArtifacts ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                  {canSubmitDeployment && (
                    <Button onClick={submitDeploymentDetails} disabled={submittingDeployment}>
                      {submittingDeployment ? 'Submitting...' : 'Submit Deployment Details'}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: 'HLD / design doc', url: proposal.hld_link || proposal.doc_link },
                    { label: 'Git Repository', url: proposal.git_repo_url },
                    { label: 'App URL', url: proposal.staging_url },
                    { label: 'Production', url: proposal.production_url },
                  ].filter((item) => item.url).map((item) => (
                    <a
                      key={item.label}
                      href={item.url!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-sm"
                    >
                      <ExternalLink className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="font-medium">{item.label}</span>
                    </a>
                  ))}
                  {!hasArtifacts && (
                    <p className="text-sm text-muted-foreground col-span-2">No project artifacts recorded yet.</p>
                  )}
                </div>
                {proposal.other_details && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Notes / deployment comments</p>
                    <p className="text-sm whitespace-pre-wrap">{proposal.other_details}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mark as Shipped */}
      {canShip && (
        <Card className="border-2 border-teal-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Rocket className="h-4 w-4 text-teal-600" />
              Mark as Shipped
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Confirm your project is live in production.
            </p>
            <div className="flex justify-end">
              <Button onClick={markShipped} disabled={shipping} className="gap-1.5">
                <Rocket className="h-3.5 w-3.5" />
                {shipping ? 'Shipping...' : 'Mark as Shipped'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy verdict (chair-posted decisions before auto-approval workflow) */}
      {canSeeVerdict && verdict && (
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gavel className="h-4 w-4 text-primary" />
              Committee Decision
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Badge variant={
              verdict.decision === 'approved' || verdict.decision === 'approved_with_conditions' ? 'success' :
              verdict.decision === 'rejected' ? 'destructive' : 'secondary'
            }>
              {VERDICT_LABELS[verdict.decision] || verdict.decision}
            </Badge>
            <p className="text-sm leading-relaxed">{verdict.rationale}</p>
            {verdict.conditions && (
              <p className="text-sm text-muted-foreground"><span className="font-medium">Conditions:</span> {verdict.conditions}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Posted {(verdict.decided_at || verdict.created_at) ? new Date(verdict.decided_at || verdict.created_at).toLocaleString() : '—'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Committee Decision Panel (in_review / legacy awaiting_decision) */}
      {isReviewer && ['in_review', 'awaiting_decision'].includes(proposal.status) && (
        <CommitteeDecisionPanel
          proposalId={id!}
          proposalStatus={proposal.status}
          submitterName={proposal.submitter_name}
          onUpdated={fetchData}
        />
      )}

      {/* Chair Override Panel — shown separately for chair to make independent decisions */}
      {isChair && (
        <ChairOverridePanel
          proposalId={id!}
          proposalStatus={proposal.status}
          onSubmitted={fetchData}
        />
      )}

      {/* Competency Level (reviewers/chair, on approved proposals) */}
      {isReviewer && ['approved', 'approved_with_conditions', 'shipped'].includes(proposal.status) && (
        <CompetencyLevelForm
          proposalId={id!}
          currentLevel={proposal.competency_level}
          onUpdated={fetchData}
        />
      )}

      {/* Comments — visible to committee and to the proposal submitter */}
      {canSeeComments && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Discussion ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {comments.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No comments yet. Start the discussion below.</p>
          )}

          {comments.map((comment) => (
            <div key={comment.id} className="p-4 rounded-lg bg-muted/40 border border-border/50 transition-colors hover:bg-muted/60">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                  {comment.author_name?.charAt(0) || 'U'}
                </div>
                <span className="text-sm font-medium">{comment.author_name}</span>
                {comment.recommendation && (
                  <Badge variant={
                    comment.recommendation === 'approve' ? 'success' :
                    comment.recommendation === 'reject' ? 'destructive' :
                    comment.recommendation === 'needs_more_info' ? 'warning' : 'default'
                  } className="text-[10px]">
                    {comment.recommendation === 'approve' ? 'Approved' :
                     comment.recommendation === 'approve_with_conditions' ? 'Approved with Conditions' :
                     comment.recommendation === 'needs_more_info' ? 'Needs More Info' :
                     comment.recommendation === 'reject' ? 'Rejected' :
                     comment.recommendation}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(comment.created_at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm pl-9 leading-relaxed">
                {comment.body.split(/(@[A-Z][\w.\-]*(?:\s[A-Z][\w.\-]*){0,3})/g).map((part, i) =>
                  /^@[A-Z]/.test(part) ? (
                    <span key={i} className="font-medium text-primary">{part}</span>
                  ) : (
                    <span key={i}>{part}</span>
                  )
                )}
              </p>
            </div>
          ))}

          <Separator className="my-4" />

          {commentsClosed ? (
            <p className="text-sm text-muted-foreground text-center py-2">Discussion is closed — this proposal has been finalized.</p>
          ) : (
          <div className="flex gap-3 items-start">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0 mt-1">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 space-y-2">
              <MentionInput
                placeholder="Add a comment... (type @ to mention someone)"
                value={newComment}
                onChange={setNewComment}
                rows={3}
                className="resize-none"
              />
              {commentError && <p className="text-xs text-destructive">{commentError}</p>}
              <div className="flex justify-end">
                <Button size="sm" onClick={postComment} disabled={!newComment.trim()} className="gap-1.5">
                  <Send className="h-3.5 w-3.5" />
                  Post Comment
                </Button>
              </div>
            </div>
          </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
